import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { openPicker } from './dateText';
import './PayrollGrid.css';

// 엑셀처럼 쓰는 그리드: 범위 선택, 방향키 이동, 바로 입력, 복사·붙여넣기(TSV), 열 고정·너비 조절.
// 값 검증과 계산은 전부 서버가 한다. 여기서는 바뀐 셀 목록만 올려 보낸다.
// 값만 다룬다 — 서식(글꼴·색)을 편집하는 기능은 없다.

const ROW_H = 30;
const OVERSCAN = 12;
const ROWNUM_W = 46;
const MIN_W = 44;
// 범위를 끌어 고를 때의 자동 스크롤: 가장자리 안쪽 EDGE px 부터, 프레임마다 BASE + 거리×ACCEL px (최대 MAX)
const DRAG_EDGE = 36;
const DRAG_BASE_SPEED = 14;
const DRAG_ACCEL = 1.1;
const DRAG_MAX_SPEED = 170;
const MAX_AUTO_W = 420;
const DEFAULT_W = { money: 104, number: 76, int: 64, date: 100, select: 84, text: 100 };
const ALWAYS_PINNED = 'name';
const STORE_PINNED = 'payroll.grid.pinned';
const STORE_WIDTHS = 'payroll.grid.widths';
const STORE_HIDDEN = 'payroll.grid.hiddenGroups';
const CELL_FONT = '13px sans-serif';
const TIP_DELAY = 1000;
const DETAIL_TIP_DELAY = 250;      // 근무타입처럼 내용을 보려고 올리는 칸은 바로 띄운다
const TIP_W = 340;

const isNumeric = (type) => type === 'money' || type === 'number' || type === 'int';
// 열 머리의 ✕ 로 뺄 수 있는 열(계약외 수당·기타 공제)
const isRemovable = (col) => col.key.startsWith('extra.') || col.key.startsWith('ded.');
// 한눈에 찾아야 하는 합계 열
const TOTAL_KEYS = new Set(['c.total', 'gross', 'monthly_pay', 'deduction_total', 'net_pay']);

// 열 묶음별 색. 머리글 띠와 계산 열 바탕에 써서 어느 구역인지 바로 보이게 한다.
const toneOf = (group) => {
  if (group.startsWith('기초')) return 'basic';
  if (group.startsWith('계약서')) return 'contract';
  if (group.startsWith('근태')) return 'attend';
  if (group.includes('지급')) return 'pay';
  if (group.includes('공제')) return 'deduct';
  if (group.startsWith('보험')) return 'insure';
  if (group.startsWith('네트')) return 'net';
  return 'note';
};

// 임금대장 플러스였을 때 저장한 키(payrollPlus.grid.*)도 읽어서, 이름을 바꾼 뒤에도 고정 열·너비 설정이 남게 한다
const load = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key) || window.localStorage.getItem(key.replace(/^payroll\./, 'payrollPlus.'));
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};
const save = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* 저장 못 해도 동작에는 지장 없다 */ }
};

const optionOf = (col, value) => (col.options || []).find((o) => o.value === value) || null;
const optionLabel = (col, value) => {
  const hit = optionOf(col, value);
  return hit ? hit.label : value == null ? '' : String(value);
};
// 선택 칸에서 고른 항목(근무타입은 색 color 와 근무시간표 rows 를 달고 온다)
const pickedOption = (row, col) => (row && col && col.type === 'select' ? optionOf(col, row.values[col.key]) : null);

// 표시 보정(서버가 계산): 칸마다 따로 반올림하면 합이 어긋나므로, 지급 항목은 맞춰 둔 정수를 보여 준다
const reconOf = (row, col) => (row && row.recon ? row.recon[col.key] : null) || null;
// 원래 값과 보이는 값이 다른 칸만 '보정된 칸'으로 표시한다
const isCorrected = (recon) => Boolean(recon) && Math.abs(recon.delta) >= 0.005;
const reconText = (value, recon) => (
  `보정: ${(Math.round(value * 100) / 100).toLocaleString('ko-KR')} → ${recon.disp.toLocaleString('ko-KR')} (${recon.delta >= 0 ? '+' : '-'}${Math.abs(recon.delta).toFixed(2)})`
);
// 금액 칸에 보이는 정수. 합계도 이 값을 더해야 화면의 숫자끼리 맞는다
const shownMoney = (value, recon) => (recon ? recon.disp : Math.round(value));

// 화면 표시용
const display = (col, value, recon) => {
  if (col.type === 'select') return optionLabel(col, value == null ? col.default : value);
  if (value == null || value === '') return '';
  if (value === 0 && col.key.startsWith('d.')) return '–';      // 공제 0원은 '–' 로 (합계에는 영향 없음)
  if (col.type === 'money') return shownMoney(value, recon).toLocaleString('ko-KR');
  if (col.type === 'number') return (Math.round(value * 100) / 100).toLocaleString('ko-KR');
  return String(value);
};

// 복사용: 엑셀이 숫자로 받도록 쉼표 없이
const copyText = (col, value, recon) => {
  if (col.type === 'select') return optionLabel(col, value == null ? col.default : value);
  if (value == null) return '';
  if (col.type === 'money') return String(shownMoney(value, recon));
  if (col.type === 'number') return String(Math.round(value * 100) / 100);
  return String(value);
};

// 편집 시작 때 입력창에 넣을 값
const editText = (col, value) => {
  if (value == null) return '';
  if (col.type === 'money') return String(Math.round(value * 100) / 100);
  return String(value);
};

// 엑셀이 클립보드에 넣는 TSV. 셀 안에 줄바꿈·탭이 있으면 큰따옴표로 감싸져 온다.
export const parseClipboard = (text) => {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === '\t') { row.push(cell); cell = ''; }
    else if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else if (ch !== '\r') cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows;
};

// 규칙이 적용된 셀 표시의 범례. 종류마다 색·무늬를 달리해 한눈에 구분되게 한다.
export const MARK_LEGEND = [
  { type: 'override', label: '직접 넣은 값(자동 계산 덮어씀)' },
  { type: 'forced', label: '보험 미적용' },
  { type: 'intern', label: '인턴 급여비율' },
  { type: 'leave', label: '휴직 제외' },
  { type: 'note', label: '근태 특이사항' },
  { type: 'net', label: '네트 역산' },
  { type: 'min-wage', label: '최저시급 미달' },
];

// 범례 전체(칸 종류 → 규칙 표시 → 보정). kind: 'key' 는 색 네모, 'mark' 는 표시 모양 그대로
const RECON_HELP = '칸마다 따로 반올림하면 합이 어긋나므로, 지급 항목의 합이 차인지급액과 맞도록 맞춘 값입니다. 저장된 값은 그대로입니다';
const LEGEND = [
  { type: 'in', kind: 'key', label: '입력' },
  { type: 'auto', kind: 'key', label: '자동 계산', note: ' (금액은 직접 넣어 덮어쓸 수 있음)' },
  { type: 'dflt', kind: 'key', label: '기본값(비워 둠)' },
  ...MARK_LEGEND.map((m) => ({ ...m, kind: 'mark' })),
  { type: 'recon', kind: 'key', label: '보정된 값', help: RECON_HELP },
];
const LEGEND_BY_TYPE = Object.fromEntries(LEGEND.map((item) => [item.type, item]));

// 범례 한 조각. 범례·상태줄·말풍선이 같은 모양을 쓴다
function LegendChip({ type, withNote = false, className = '' }) {
  const item = LEGEND_BY_TYPE[type];
  if (!item) return null;
  if (item.kind === 'mark') return <span className={`prg-mark mark-${type} ${className}`}>{item.label}</span>;
  return <span className={`prg-key ${className}`} title={item.help}><i className={type} />{item.label}{withNote ? item.note || '' : ''}</span>;
}

// 한 칸에 적용된 범례 항목과 그 이유: [{type, text}]. 칸 종류(입력·자동·기본값) → 규칙 표시 → 보정 순서
const cellTags = (row, col) => {
  if (!row || !col) return [];
  const mark = row.marks ? row.marks[col.key] : null;
  const recon = reconOf(row, col);
  const tags = [];
  if ((row.fallback || []).includes(col.key)) tags.push({ type: 'dflt', text: col.hint || '비워 두어 기본값이 보입니다' });
  else if (!col.editable) tags.push({ type: 'auto', text: ['수정할 수 없는 계산 값입니다.', col.hint].filter(Boolean).join(' ') });
  else if (col.auto && !(mark && mark.type === 'override')) tags.push({ type: 'auto', text: col.hint || '' });
  else if (!col.auto) tags.push({ type: 'in', text: col.hint || '' });
  if (mark) tags.push({ type: mark.type, text: mark.text });
  if (isCorrected(recon)) tags.push({ type: 'recon', text: reconText(row.values[col.key], recon) });
  return tags;
};

let measureCanvas = null;
const textWidth = (text, font) => {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  ctx.font = font;
  return ctx.measureText(text).width;
};

// 열 머리 둘째 줄의 꼬리표(통상·비과세·자동 등). 종류마다 색을 달리해 한눈에 구분되게 한다
const TAG_TONE = { 통상: 'ordinary', 비통상: 'non-ordinary', 기본: 'plain', 비과세: 'tax-free', 계약외: 'extra', 자동: 'auto', '자동(수정불가)': 'locked' };
// 열 머리에 보일 이름의 줄들(서버가 두 줄로 나눠 준 이름은 head 에 줄바꿈이 들어 있다)
const headLines = (col) => String(col.head || col.label).split('\n');
// 컬럼 표시에서 다른 갈래 아래에 넣어 보일 묶음
const CATEGORY_OF = { 근태: '임금대장' };
const subTags = (col) => [
  ...(col.sub ? col.sub.split(' · ') : []),
  col.auto ? '자동' : col.editable ? '' : '자동(수정불가)',
].filter(Boolean);

// 묶음 띠에 붙는 추가 버튼: 계약서 근로조건 → 근무타입, 계약서 시수·금액 → 계약서 수당, 지급 → 계약외 수당, 공제 → 공제항목
const addButtonOf = (group) => {
  if (group.startsWith('계약서')) {
    if (group.includes('근로조건')) return { kind: 'worktype', label: '+ 근무타입', title: '이 달 대장에 근무타입을 추가합니다' };
    if (group.includes('시수')) return { kind: 'wage', label: '+ 시수 항목', title: '계약서 시수 항목(시수연동 수당)을 이 달 대장에 추가합니다' };
    if (group.includes('금액')) return { kind: 'fixed', label: '+ 금액 항목', title: '계약서 금액 항목(고정수당)을 이 달 대장에 추가합니다' };
    return null;
  }
  const tone = toneOf(group);
  if (tone === 'pay') return { kind: 'extra', label: '+ 계약외 수당', title: '이 달 대장에 열을 추가합니다' };
  if (tone === 'deduct') return { kind: 'ded', label: '+ 공제항목', title: '이 달 대장에 열을 추가합니다' };
  return null;
};

function PayrollGrid({ columns: allColumns, rows, onCommit, onAddRows, onSelectionChange, onUndo, onRedo, onRemoveColumn, onAddColumn, jump, busy, memos, onOpenMemo, memoRowId, onOpenLeave, leaveRowId, toolbar, empty }) {
  // hidden 열은 값만 저장되고(네트 역산처럼 패널에서 넣는 값) 표에는 나오지 않는다
  const columns = useMemo(() => allColumns.filter((c) => !c.hidden), [allColumns]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const selectRef = useRef(null);
  const dragging = useRef(false);
  const resizing = useRef(false);
  const hoverStyle = useRef(null);
  const hoverCol = useRef(-1);
  // 칸에 1초 머물면 그 칸에 적용된 범례를 말풍선으로 보인다
  const hoverCell = useRef('');
  const tipTimer = useRef(0);
  const [tip, setTip] = useState(null);       // {r, c, x, y, flip}
  const [memoTip, setMemoTip] = useState(null);   // 메모 단추에 마우스를 올리면 바로 보이는 미리보기 {x, y, memo}
  const hideTip = useCallback(() => {
    window.clearTimeout(tipTimer.current);
    hoverCell.current = '';
    setTip((prev) => (prev ? null : prev));
  }, []);
  useEffect(() => () => window.clearTimeout(tipTimer.current), []);
  const pointer = useRef(null);
  const autoScroll = useRef(0);
  const gridFocused = useRef(false);
  const [viewport, setViewport] = useState({ top: 0, height: 600 });
  const [anchor, setAnchor] = useState({ r: 0, c: 0 });
  const [active, setActive] = useState({ r: 0, c: 0 });
  const [editing, setEditingState] = useState(null); // {r, c, text, select}
  // 포커스를 옮기는 순간 blur 가 같은 렌더의 핸들러로 다시 들어오므로, 최신 편집 상태는 ref 로 본다
  const editingRef = useRef(null);
  const setEditing = useCallback((next) => { editingRef.current = next; setEditingState(next); }, []);
  const [pinnedKeys, setPinnedKeys] = useState(() => load(STORE_PINNED, []));
  const [customWidths, setCustomWidths] = useState(() => load(STORE_WIDTHS, {}));
  const [hiddenGroups, setHiddenGroups] = useState(() => load(STORE_HIDDEN, []));

  // 열 묶음 목록(원래 순서) — 묶음 단위로 접었다 펼 수 있다
  const groupNames = useMemo(() => [...new Set(columns.map((c) => c.group))], [columns]);
  // 묶음을 큰 갈래(기초·계약서·근태·임금대장 …)로 모은다: '계약서 · 금액' → 계약서 아래의 '금액'
  const categories = useMemo(() => {
    const out = [];
    groupNames.forEach((name) => {
      const [first, ...tail] = name.split(' · ');
      const head = CATEGORY_OF[first] || first;      // 근태는 임금대장 갈래에 넣어 보인다
      let cat = out.find((x) => x.name === head);
      if (!cat) { cat = { name: head, groups: [] }; out.push(cat); }
      cat.groups.push({ name, label: tail.join(' · ') || first });
    });
    return out;
  }, [groupNames]);
  const toggleGroup = (name) => {
    setHiddenGroups((prev) => {
      const next = prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name];
      save(STORE_HIDDEN, next);
      return next;
    });
  };

  // 갈래 이름(계약서·임금대장)을 누르면 그 갈래를 통째로 끄거나 켠다: 하나라도 꺼져 있으면 모두 켜고, 모두 켜져 있으면 모두 끈다
  const toggleCategory = (cat) => {
    const names = cat.groups.map((g) => g.name);
    setHiddenGroups((prev) => {
      const allOn = names.every((n) => !prev.includes(n));
      const next = allOn ? [...prev, ...names] : prev.filter((g) => !names.includes(g));
      save(STORE_HIDDEN, next);
      return next;
    });
  };

  // 고정한 열을 왼쪽에 쌓는다: 성명(항상) → 고정한 순서대로 → 나머지는 원래 순서
  const { cols, pinCount } = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const order = [ALWAYS_PINNED, ...pinnedKeys.filter((k) => k !== ALWAYS_PINNED)].filter((k) => byKey.has(k));
    const pinned = order.map((k) => byKey.get(k));
    // 접은 묶음의 열은 뺀다(고정한 열은 묶음을 접어도 남는다)
    const rest = columns.filter((c) => !order.includes(c.key) && !hiddenGroups.includes(c.group));
    return { cols: [...pinned, ...rest], pinCount: pinned.length };
  }, [columns, pinnedKeys, hiddenGroups]);

  const widths = useMemo(() => cols.map((c) => {
    if (customWidths[c.key]) return customWidths[c.key];
    // 기본 너비: 머리글이 잘리지 않을 만큼은 확보한다
    const label = Math.ceil(Math.max(...headLines(c).map((line) => textWidth(line, `600 ${CELL_FONT}`)))) + 30;
    return Math.max(c.width || DEFAULT_W[c.type] || 100, Math.min(label, 180));
  }), [cols, customWidths]);

  // 표 안에서 각 열의 왼쪽 좌표(순번 열 포함), 고정 구역의 폭
  const lefts = useMemo(() => {
    const out = [];
    let x = ROWNUM_W;
    widths.forEach((w) => { out.push(x); x += w; });
    return out;
  }, [widths]);
  const pinnedW = pinCount ? lefts[pinCount - 1] + widths[pinCount - 1] : ROWNUM_W;
  const tableW = ROWNUM_W + widths.reduce((a, b) => a + b, 0);

  // 머리글 1행: 고정 열은 하나씩, 나머지는 같은 묶음이 이어지는 구간을 합친다
  const groups = useMemo(() => {
    const out = [];
    cols.forEach((c, i) => {
      const last = out[out.length - 1];
      if (i >= pinCount && i > pinCount && last && last.name === c.group && !last.pinned) last.span += 1;
      else out.push({ name: c.group, span: 1, start: i, pinned: i < pinCount });
    });
    return out;
  }, [cols, pinCount]);
  const groupStarts = useMemo(() => new Set(groups.map((g) => g.start)), [groups]);

  // 바로가기: 그 묶음의 첫 열이 고정 열 바로 오른쪽에 오도록 가로로 민다
  const jumpTargets = useMemo(() => groups.filter((g) => !g.pinned), [groups]);
  const scrollToGroup = (g) => {
    const el = scrollRef.current;
    if (el && g) el.scrollTo({ left: Math.max(0, lefts[g.start] - pinnedW), behavior: 'smooth' });
  };

  const range = useMemo(() => ({
    r0: Math.min(anchor.r, active.r), r1: Math.max(anchor.r, active.r),
    c0: Math.min(anchor.c, active.c), c1: Math.max(anchor.c, active.c),
  }), [anchor, active]);

  // 이벤트 핸들러(창 단위 마우스 이동 등)에서 최신 배치를 보기 위한 사본
  const geo = useRef({});
  geo.current = { widths, lefts, pinCount, pinnedW, rowCount: rows.length, colCount: cols.length };

  // 행·열이 줄면 선택이 범위를 벗어나지 않게 당긴다
  useEffect(() => {
    const clamp = (p) => ({
      r: Math.max(0, Math.min(p.r, rows.length - 1)),
      c: Math.max(0, Math.min(p.c, cols.length - 1)),
    });
    setAnchor((p) => { const n = clamp(p); return n.r === p.r && n.c === p.c ? p : n; });
    setActive((p) => { const n = clamp(p); return n.r === p.r && n.c === p.c ? p : n; });
  }, [rows.length, cols.length]);

  // 선택 범위와, 열 고정으로 순서가 바뀌어도 알 수 있게 활성 셀의 행·열 식별자를 함께 알린다
  const activeRowId = rows[active.r] ? rows[active.r].id : null;
  const activeKey = cols[active.c] ? cols[active.c].key : null;
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(range, { rowId: activeRowId, key: activeKey });
  }, [range, activeRowId, activeKey, onSelectionChange]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewport((v) => ({ ...v, height: el.clientHeight }));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const first = Math.max(0, Math.floor(viewport.top / ROW_H) - OVERSCAN);
  const last = Math.min(rows.length, Math.ceil((viewport.top + viewport.height) / ROW_H) + OVERSCAN);

  const headHeight = () => {
    const el = scrollRef.current;
    const head = el ? el.querySelector('thead') : null;
    return head ? head.offsetHeight : 0;
  };

  const scrollIntoView = useCallback((r, c, center) => {
    const el = scrollRef.current;
    if (!el) return;
    const g = geo.current;
    const headH = headHeight();
    const top = r * ROW_H;
    const isPinned = c < g.pinCount;       // 고정 열은 늘 보이므로 가로로는 옮기지 않는다
    const x = g.lefts[c];
    const w = g.widths[c];
    if (center) {
      // 멀리 있는 셀로 건너뛸 때는 가운데쯤 오게 부드럽게 옮긴다. 이미 보이는 방향은 그대로 둔다.
      const rowVisible = top >= el.scrollTop && top + ROW_H <= el.scrollTop + el.clientHeight - headH;
      const colVisible = isPinned || (x - g.pinnedW >= el.scrollLeft && x + w <= el.scrollLeft + el.clientWidth);
      el.scrollTo({
        top: rowVisible ? el.scrollTop : Math.max(0, top - (el.clientHeight - headH) / 2),
        left: colVisible ? el.scrollLeft : Math.max(0, x - g.pinnedW - (el.clientWidth - g.pinnedW) / 2 + w / 2),
        behavior: 'smooth',
      });
      return;
    }
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + ROW_H > el.scrollTop + el.clientHeight - headH) el.scrollTop = top + ROW_H - el.clientHeight + headH;
    if (isPinned) return;
    if (x - g.pinnedW < el.scrollLeft) el.scrollLeft = x - g.pinnedW;
    else if (x + w > el.scrollLeft + el.clientWidth) el.scrollLeft = x + w - el.clientWidth;
  }, []);

  // 되돌리기·다시 실행·이력에서 고른 변경: 영향받은 셀 범위를 선택하고 그 자리로 화면을 옮긴다
  const jumpNonce = jump ? jump.nonce : null;
  useEffect(() => {
    if (!jump) return;
    const rs = jump.cells.map((cell) => rows.findIndex((row) => row.id === cell.row_id)).filter((i) => i >= 0);
    const cs = jump.cells.map((cell) => cols.findIndex((col) => col.key === cell.key)).filter((i) => i >= 0);
    if (!rs.length || !cs.length) return;
    const top = { r: Math.min(...rs), c: Math.min(...cs) };
    gridFocused.current = true;
    setAnchor(top);
    setActive({ r: Math.max(...rs), c: Math.max(...cs) });
    scrollIntoView(top.r, top.c, true);
  }, [jumpNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // 활성 셀에는 항상 입력창이 들어 있고(평소엔 투명), 그리드를 쓰는 동안 포커스는 늘 그 입력창에 둔다.
  // 글자를 치는 순간 이미 입력창 안이므로 한글 조합이 첫 글자부터 정상으로 시작된다
  // (입력창이 아닌 곳에서 첫 키를 받으면 IME 가 조합을 못 해 영문이 찍힌다).
  const isSelectEditing = Boolean(editing && editing.select);
  useLayoutEffect(() => {
    if (!gridFocused.current) return;
    const target = isSelectEditing ? selectRef.current : inputRef.current;
    if (target) {
      if (document.activeElement !== target) target.focus({ preventScroll: true });
    } else if (scrollRef.current && document.activeElement !== scrollRef.current) {
      scrollRef.current.focus({ preventScroll: true });   // 활성 행이 화면 밖(가상 스크롤)일 때
    }
  }, [active.r, active.c, isSelectEditing, first, last, rows, cols]);

  const moveTo = useCallback((r, c, extend) => {
    const next = {
      r: Math.max(0, Math.min(r, rows.length - 1)),
      c: Math.max(0, Math.min(c, cols.length - 1)),
    };
    setActive(next);
    if (!extend) setAnchor(next);
    scrollIntoView(next.r, next.c);
  }, [rows.length, cols.length, scrollIntoView]);

  const currentText = useCallback((r, c) => {
    const col = cols[c];
    const row = rows[r];
    // 자동값이 보이는 칸은 빈 칸에서 시작한다(자동값을 그대로 저장하지 않도록)
    const shown = (row.fallback || []).includes(col.key) || (row.auto || []).includes(col.key);
    return shown ? '' : editText(col, row.values[col.key]);
  }, [cols, rows]);

  const startEdit = useCallback((r, c, text) => {
    const col = cols[c];
    const row = rows[r];
    if (!col || !row || !col.editable || busy) return;
    gridFocused.current = true;
    if (col.type === 'select') {
      setEditing({ r, c, text: currentText(r, c), select: true });
      return;
    }
    const value = text != null ? text : currentText(r, c);
    setEditing({ r, c, text: value });
    if (text == null) {
      // F2·더블클릭: 기존 값 끝에 커서
      window.requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) el.setSelectionRange(value.length, value.length);
      });
    }
  }, [cols, rows, busy, setEditing, currentText]);

  const commitEdit = useCallback((move) => {
    const edit = editingRef.current;
    if (!edit) return;
    const col = cols[edit.c];
    const row = rows[edit.r];
    setEditing(null);
    if (edit.text !== currentText(edit.r, edit.c)) onCommit([{ row_id: row.id, key: col.key, value: edit.text }]);
    if (move) moveTo(edit.r + move.dr, edit.c + move.dc, false);
  }, [cols, rows, onCommit, moveTo, setEditing, currentText]);

  const clearRange = useCallback(() => {
    const changes = [];
    for (let r = range.r0; r <= range.r1; r += 1) {
      for (let c = range.c0; c <= range.c1; c += 1) {
        if (cols[c].editable && rows[r]) changes.push({ row_id: rows[r].id, key: cols[c].key, value: null });
      }
    }
    if (changes.length) onCommit(changes);
  }, [range, cols, rows, onCommit]);

  const handleCopy = (e) => {
    if (editing) return;
    const lines = [];
    for (let r = range.r0; r <= range.r1; r += 1) {
      const cells = [];
      for (let c = range.c0; c <= range.c1; c += 1) cells.push(copyText(cols[c], rows[r] ? rows[r].values[cols[c].key] : null, reconOf(rows[r], cols[c])));
      lines.push(cells.join('\t'));
    }
    e.clipboardData.setData('text/plain', lines.join('\n'));
    e.preventDefault();
  };

  const handlePaste = async (e) => {
    if (editing || busy) return;
    const text = e.clipboardData.getData('text/plain');
    e.preventDefault();
    const data = parseClipboard(text);
    if (!data.length) return;
    // 한 칸을 복사해 범위에 붙이면 범위 전체를 채운다
    const single = data.length === 1 && data[0].length === 1;
    const height = single ? range.r1 - range.r0 + 1 : data.length;
    const width = single ? range.c1 - range.c0 + 1 : Math.max(...data.map((d) => d.length));

    let target = rows;
    const overflow = range.r0 + height - rows.length;
    if (overflow > 0) {
      // 마지막 행 아래로 넘치는 만큼 행을 먼저 만든다
      const created = await onAddRows(overflow);
      if (!created) return;
      target = [...rows, ...created];
    }
    const changes = [];
    for (let i = 0; i < height; i += 1) {
      for (let j = 0; j < width; j += 1) {
        const col = cols[range.c0 + j];
        const row = target[range.r0 + i];
        if (!col || !row || !col.editable) continue;
        const value = single ? data[0][0] : (data[i] || [])[j];
        if (value === undefined) continue;
        changes.push({ row_id: row.id, key: col.key, value });
      }
    }
    if (changes.length) onCommit(changes);
    setAnchor({ r: range.r0, c: range.c0 });
    setActive({ r: Math.min(range.r0 + height - 1, target.length - 1), c: Math.min(range.c0 + width - 1, cols.length - 1) });
  };

  // 편집 중이 아닐 때의 키: 이동·지우기·되돌리기. 글자 키는 그대로 두면 입력창에 들어가 편집이 시작된다.
  const handleNavKey = (e) => {
    const { r, c } = active;
    const ctrl = e.ctrlKey || e.metaKey;
    const jumpTo = (dr, dc) => {
      // Ctrl+방향키: 끝까지
      if (ctrl) moveTo(dr ? (dr > 0 ? rows.length - 1 : 0) : r, dc ? (dc > 0 ? cols.length - 1 : 0) : c, e.shiftKey);
      else moveTo(r + dr, c + dc, e.shiftKey);
      e.preventDefault();
    };
    const page = Math.max(1, Math.floor(viewport.height / ROW_H) - 2);
    switch (e.key) {
      case 'ArrowUp': jumpTo(-1, 0); return;
      case 'ArrowDown': jumpTo(1, 0); return;
      case 'ArrowLeft': jumpTo(0, -1); return;
      case 'ArrowRight': jumpTo(0, 1); return;
      case 'Tab': moveTo(r, c + (e.shiftKey ? -1 : 1), false); e.preventDefault(); return;
      case 'Enter': moveTo(r + (e.shiftKey ? -1 : 1), c, false); e.preventDefault(); return;
      case 'Home': moveTo(ctrl ? 0 : r, 0, e.shiftKey); e.preventDefault(); return;
      case 'End': moveTo(ctrl ? rows.length - 1 : r, cols.length - 1, e.shiftKey); e.preventDefault(); return;
      case 'PageDown': moveTo(r + page, c, e.shiftKey); e.preventDefault(); return;
      case 'PageUp': moveTo(r - page, c, e.shiftKey); e.preventDefault(); return;
      case 'F2': startEdit(r, c); e.preventDefault(); return;
      case 'Delete':
      case 'Backspace': clearRange(); e.preventDefault(); return;
      default:
    }
    if (ctrl && (e.key === 'z' || e.key === 'Z')) {
      if (e.shiftKey) { if (onRedo) onRedo(); } else if (onUndo) onUndo();
      e.preventDefault();
      return;
    }
    if (ctrl && (e.key === 'y' || e.key === 'Y')) {
      if (onRedo) onRedo();
      e.preventDefault();
      return;
    }
    if (ctrl && (e.key === 'a' || e.key === 'A')) {
      setAnchor({ r: 0, c: 0 });
      setActive({ r: rows.length - 1, c: cols.length - 1 });
      e.preventDefault();
      return;
    }
    // 선택형 열은 글자를 치면 목록을 연다
    if (!ctrl && !e.altKey && e.key.length === 1 && cols[c] && cols[c].type === 'select') {
      startEdit(r, c);
      e.preventDefault();
    }
  };

  const handleEditKey = (e) => {
    if (e.nativeEvent.isComposing) return;     // 한글 조합 중의 Enter 는 조합 확정이다
    if (e.key === 'Enter') { commitEdit({ dr: e.shiftKey ? -1 : 1, dc: 0 }); e.preventDefault(); }
    else if (e.key === 'Tab') { commitEdit({ dr: 0, dc: e.shiftKey ? -1 : 1 }); e.preventDefault(); }
    else if (e.key === 'Escape') { setEditing(null); e.preventDefault(); }
  };

  const onInputKeyDown = (e) => {
    if (editingRef.current) handleEditKey(e);
    else handleNavKey(e);
    e.stopPropagation();
  };

  const onInputChange = (e) => {
    const value = e.target.value;
    const edit = editingRef.current;
    if (edit) { setEditing({ ...edit, text: value }); return; }
    const col = cols[active.c];
    if (!col || !col.editable || busy || col.type === 'select') return;
    setEditing({ r: active.r, c: active.c, text: value });    // 첫 글자(조합 중인 한글 포함)부터 편집 시작
  };

  // ---- 마우스: 범위 선택. 끌다가 가장자리를 넘으면 그 방향으로 화면이 따라 움직인다.
  const cellFromPoint = (x, y) => {
    const el = scrollRef.current;
    const g = geo.current;
    const rect = el.getBoundingClientRect();
    const r = Math.floor((y - rect.top - headHeight() + el.scrollTop) / ROW_H);
    const localX = x - rect.left;
    const tableX = localX < g.pinnedW ? localX : localX + el.scrollLeft;
    let c = localX < g.pinnedW ? 0 : g.pinCount;
    const end = localX < g.pinnedW ? g.pinCount : g.colCount;
    for (let i = c; i < end; i += 1) { if (tableX >= g.lefts[i]) c = i; }
    return {
      r: Math.max(0, Math.min(r, g.rowCount - 1)),
      c: Math.max(0, Math.min(c, g.colCount - 1)),
    };
  };

  useEffect(() => {
    const followPointer = () => {
      if (!pointer.current) return;
      const next = cellFromPoint(pointer.current.x, pointer.current.y);
      setActive((p) => (p.r === next.r && p.c === next.c ? p : next));
    };
    const step = () => {
      autoScroll.current = 0;
      const el = scrollRef.current;
      if (!dragging.current || !el || !pointer.current) return;
      const rect = el.getBoundingClientRect();
      const { x, y } = pointer.current;
      const topEdge = rect.top + headHeight();
      const leftEdge = rect.left + geo.current.pinnedW;
      // 가장자리를 넘은 거리에 비례해 빨라진다
      // 가장자리 안쪽 EDGE px 부터 밀기 시작하고, 가장자리를 넘어갈수록 빨라진다
      const speed = (d) => Math.sign(d) * Math.min(DRAG_MAX_SPEED, Math.ceil(DRAG_BASE_SPEED + Math.abs(d) * DRAG_ACCEL));
      const bottom = rect.bottom - DRAG_EDGE;
      const right = rect.right - DRAG_EDGE;
      const dy = y < topEdge + DRAG_EDGE ? speed(y - topEdge - DRAG_EDGE) : y > bottom ? speed(y - bottom) : 0;
      const dx = x < leftEdge && x > rect.left + ROWNUM_W && y >= topEdge
        ? 0     // 고정 열 위에 있으면 가로로는 밀지 않는다(고정 열을 선택하려는 것)
        : x < leftEdge + DRAG_EDGE ? speed(x - leftEdge - DRAG_EDGE) : x > right ? speed(x - right) : 0;
      if (dx || dy) {
        el.scrollLeft += dx;
        el.scrollTop += dy;
        followPointer();
        autoScroll.current = window.requestAnimationFrame(step);
      }
    };
    const onMove = (e) => {
      if (!dragging.current) return;
      pointer.current = { x: e.clientX, y: e.clientY };
      followPointer();
      if (!autoScroll.current) autoScroll.current = window.requestAnimationFrame(step);
    };
    const onUp = () => {
      dragging.current = false;
      pointer.current = null;
      if (autoScroll.current) window.cancelAnimationFrame(autoScroll.current);
      autoScroll.current = 0;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (autoScroll.current) window.cancelAnimationFrame(autoScroll.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onCellMouseDown = (r, c, e) => {
    hideTip();
    if (e.button !== 0) return;
    if (editing) {
      if (editing.r === r && editing.c === c) return;   // 편집 중인 칸 안의 클릭은 커서 이동
      commitEdit();
    }
    gridFocused.current = true;
    dragging.current = true;
    setActive({ r, c });
    if (!e.shiftKey) setAnchor({ r, c });
    e.preventDefault();
    // preventDefault 로 포커스가 안 옮겨지므로, 같은 칸을 다시 눌렀을 때도 입력창에 포커스를 돌려 준다
    if (inputRef.current && r === active.r && c === active.c) inputRef.current.focus({ preventScroll: true });
  };

  const selectRow = (r, e) => {
    if (editing) commitEdit();
    gridFocused.current = true;
    setAnchor({ r: e.shiftKey ? anchor.r : r, c: 0 });
    setActive({ r, c: cols.length - 1 });
    e.preventDefault();
  };

  // 열 머리를 누르면 그 열 전체를 고른다(Shift 로 여러 열). 맨 위 칸이 활성 칸이 된다
  const selectCol = (c, e) => {
    if (resizing.current || !rows.length) return;
    if (editing) commitEdit();
    gridFocused.current = true;
    setAnchor({ r: rows.length - 1, c: e.shiftKey ? anchor.c : c });
    setActive({ r: 0, c });
    if (scrollRef.current) scrollRef.current.focus({ preventScroll: true });
  };

  // 마우스가 올라간 열을 띠로 보인다. 열이 바뀔 때마다 표 전체를 다시 그리지 않도록 스타일 한 줄만 바꾼다
  const showHoverCol = (index) => {
    if (hoverCol.current === index) return;
    hoverCol.current = index;
    if (hoverStyle.current) {
      hoverStyle.current.textContent = index < 0 ? ''
        : `.prg-table tbody tr td.prg-cell:nth-child(${index + 1}) { background-image: linear-gradient(var(--hover), var(--hover)); }`;
    }
  };
  const onBodyHover = (e) => {
    const td = e.target.closest ? e.target.closest('td.prg-cell') : null;
    showHoverCol(td ? td.cellIndex : -1);
    const id = td ? `${td.parentElement.dataset.r}:${td.cellIndex - 1}` : '';
    if (id === hoverCell.current) return;
    hideTip();
    hoverCell.current = id;
    if (!td || dragging.current) return;
    const r = Number(td.parentElement.dataset.r);
    const c = td.cellIndex - 1;
    const detail = pickedOption(rows[r], cols[c]);
    tipTimer.current = window.setTimeout(() => {
      const rect = td.getBoundingClientRect();
      const flip = rect.left > window.innerWidth - TIP_W - 16;      // 오른쪽 끝에서는 왼쪽으로 펼친다
      setTip({ r, c, x: flip ? rect.right : rect.left, y: rect.bottom + 4, flip });
    }, detail && detail.rows ? DETAIL_TIP_DELAY : TIP_DELAY);
  };

  // ---- 열 고정
  const togglePin = (key) => {
    setPinnedKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      save(STORE_PINNED, next);
      return next;
    });
  };

  // ---- 열 너비: 머리글 오른쪽 경계를 끌어 조절, 두 번 누르면 내용에 맞춤
  const startResize = (c, e) => {
    // 너비를 끌다 놓을 때 생기는 클릭이 열 고정으로 이어지지 않게 한다
    resizing.current = true;
    window.addEventListener('mouseup', () => setTimeout(() => { resizing.current = false; }, 0), { once: true });
    e.preventDefault();
    e.stopPropagation();
    const key = cols[c].key;
    const startX = e.clientX;
    const startW = widths[c];
    let latest = startW;
    const onMove = (ev) => {
      latest = Math.max(MIN_W, Math.round(startW + ev.clientX - startX));
      setCustomWidths((prev) => ({ ...prev, [key]: latest }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      setCustomWidths((prev) => { save(STORE_WIDTHS, prev); return prev; });
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const autoFit = (c, e) => {
    e.preventDefault();
    e.stopPropagation();
    const col = cols[c];
    let widest = Math.max(Math.max(...headLines(col).map((line) => textWidth(line, `600 ${CELL_FONT}`))) + 34, textWidth(subTags(col).join('  '), '10.5px sans-serif') + 14 + subTags(col).length * 8);
    rows.forEach((row) => { widest = Math.max(widest, textWidth(display(col, row.values[col.key], reconOf(row, col)), CELL_FONT) + 16); });
    const width = Math.min(MAX_AUTO_W, Math.max(MIN_W, Math.ceil(widest)));
    setCustomWidths((prev) => { const next = { ...prev, [col.key]: width }; save(STORE_WIDTHS, next); return next; });
  };

  // 선택 범위 합계 (엑셀 상태 표시줄)
  const summary = useMemo(() => {
    let sum = 0;
    let count = 0;
    let numbers = 0;
    for (let r = range.r0; r <= range.r1 && r < rows.length; r += 1) {
      for (let c = range.c0; c <= range.c1 && c < cols.length; c += 1) {
        const v = rows[r].values[cols[c].key];
        if (v == null || v === '') continue;
        count += 1;
        if (isNumeric(cols[c].type) && typeof v === 'number') {
          sum += cols[c].type === 'money' ? shownMoney(v, reconOf(rows[r], cols[c])) : v;
          numbers += 1;
        }
      }
    }
    return { sum, count, numbers };
  }, [range, rows, cols]);

  // 합계 행: 금액 열만 더한다. 화면에 보이는 정수를 더한다(위 칸들을 더한 값과 같아야 하므로)
  const totals = useMemo(() => {
    const out = {};
    cols.forEach((col) => {
      if (col.type !== 'money') return;
      let sum = 0;
      let any = false;
      rows.forEach((row) => {
        const v = row.values[col.key];
        if (typeof v === 'number') { sum += shownMoney(v, reconOf(row, col)); any = true; }
      });
      if (any) out[col.key] = sum;
    });
    return out;
  }, [cols, rows]);

  const activeCol = cols[active.c];
  const activeRow = rows[active.r];
  // 선택한 칸에 적용된 범례 항목 (범례 강조·상태줄)
  const activeTags = useMemo(() => cellTags(activeRow, activeCol), [activeRow, activeCol]);
  const activeTypes = useMemo(() => new Set(activeTags.map((t) => t.type)), [activeTags]);
  const tipRow = tip ? rows[tip.r] : null;
  const tipCol = tip ? cols[tip.c] : null;
  const tipTags = tipRow && tipCol ? cellTags(tipRow, tipCol) : [];
  const tipOption = pickedOption(tipRow, tipCol);

  const stickyStyle = (c) => (c < pinCount ? { left: lefts[c] } : undefined);
  const pinClass = (c) => (c < pinCount ? `pinned ${c === pinCount - 1 ? 'pin-edge' : ''}` : '');

  const renderCell = (row, r, col, c) => {
    const inRange = r >= range.r0 && r <= range.r1 && c >= range.c0 && c <= range.c1;
    const isActive = r === active.r && c === active.c;
    const isEditing = Boolean(editing && editing.r === r && editing.c === c);
    const fallback = (row.fallback || []).includes(col.key);
    const mark = row.marks ? row.marks[col.key] : null;
    const recon = reconOf(row, col);
    const corrected = isCorrected(recon);
    const cls = [
      'prg-cell',
      `tone-${toneOf(col.group)}`,
      isNumeric(col.type) ? 'num' : '',
      col.editable && !col.auto ? '' : 'calc',
      TOTAL_KEYS.has(col.key) ? 'total' : '',
      fallback ? 'fallback' : '',
      mark ? `mark mark-${mark.type}` : '',
      corrected ? 'recon' : '',
      r === active.r || c === active.c ? 'cross' : '',      // 선택한 칸의 행·열 띠
      inRange ? 'sel' : '',
      // 선택 범위의 바깥 테두리
      inRange && r === range.r0 ? 'sel-t' : '',
      inRange && r === range.r1 ? 'sel-b' : '',
      inRange && c === range.c0 ? 'sel-l' : '',
      inRange && c === range.c1 ? 'sel-r' : '',
      isActive ? 'active' : '',
      pinClass(c),
      groupStarts.has(c) ? 'group-start' : '',
    ].filter(Boolean).join(' ');

    // 고른 항목에 색이 있으면(근무타입) 칸을 그 색으로 칠한다
    const picked = pickedOption(row, col);
    const tint = picked && picked.color ? { backgroundColor: picked.color } : null;

    let body = display(col, row.values[col.key], recon);
    if (col.key === ALWAYS_PINNED) {
      // 성명 왼쪽의 메모 단추: 메모는 달이 아니라 직원에게 붙는다. 메모가 있으면 노란 단추, 없으면 흰 단추
      const memo = memos && row.employee_id ? memos[row.employee_id] : null;
      body = (
        <>
          {onOpenMemo && body !== '' && (
            <button
              type="button"
              className={`prg-memo ${memo ? 'has' : ''} ${memoRowId === row.id ? 'open' : ''}`}
              aria-label={memo ? '메모 보기·고치기' : '메모 쓰기'}
              onMouseDown={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setMemoTip(null); onOpenMemo(row); }}
              onMouseEnter={(e) => {
                hideTip();
                if (!memo) return;
                const rect = e.currentTarget.getBoundingClientRect();
                setMemoTip({ x: rect.left, y: rect.bottom + 4, memo });
              }}
              onMouseLeave={() => setMemoTip(null)}
            >
              ≡
            </button>
          )}
          {body}
          {(row.badges || []).map((b) => (
            <span
              key={b}
              className={`prg-badge ${b === '입사' ? 'join' : b === '휴직' ? 'absent' : 'leave'}`}
              title={b === '휴직' ? (row.leaves_now || []).map((x) => `${x.reason || '휴직'} ${x.start} ~ ${x.end || '계속'}`).join(' / ') : undefined}
            >
              {b}
            </span>
          ))}
        </>
      );
    }
    if (col.type === 'leaves' && onOpenLeave && row.values.name) {
      // 휴직관리: 건수를 보여 주고, 누르면 관리 창이 열린다(다시 누르면 닫힌다)
      const count = row.values[col.key] || 0;
      body = (
        <button
          type="button"
          className={`prg-leave ${count ? 'has' : ''} ${leaveRowId === row.id ? 'open' : ''}`}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onOpenLeave(row); }}
        >
          {count ? `휴직 ${count}건` : '+ 휴직'}
        </button>
      );
    }
    if (isActive) {
      body = (
        <>
          {isEditing && editing.select ? (
            <select
              ref={selectRef}
              className="prg-input"
              value={editing.text}
              onChange={(e) => {
                const value = e.target.value;
                if (!editingRef.current) return;
                setEditing(null);
                onCommit([{ row_id: row.id, key: col.key, value: value === '' ? null : value }]);
              }}
              onBlur={() => setEditing(null)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setEditing(null); e.preventDefault(); }
                if (e.key === 'Tab') { setEditing(null); moveTo(r, c + (e.shiftKey ? -1 : 1), false); e.preventDefault(); }
                e.stopPropagation();
              }}
            >
              <option value="">{col.default != null ? `(기본: ${optionLabel(col, col.default)})` : '(없음)'}</option>
              {(col.options || []).map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
            </select>
          ) : (
            <span className={isEditing ? 'prg-val hidden' : 'prg-val'}>{body}</span>
          )}
          {/* 항상 있는 입력창: 평소엔 투명한 채로 포커스만 갖고 있다가, 글자가 들어오면 그대로 편집창이 된다 */}
          <input
            ref={inputRef}
            className={`prg-input ${isEditing && !editing.select ? 'editing' : 'ghost'}`}
            value={isEditing && !editing.select ? editing.text : ''}
            readOnly={!col.editable || busy || col.type === 'select'}
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            onBlur={() => { if (editingRef.current && !editingRef.current.select) commitEdit(); }}
            autoComplete="off"
            spellCheck={false}
            aria-label={col.label}
          />
          {/* 날짜 칸: 직접 적어도 되고(260321·0321·21), 달력에서 골라도 된다 */}
          {col.type === 'date' && col.editable && !busy && (
            <>
              <button
                type="button"
                className="prg-cal"
                tabIndex={-1}
                title="달력에서 고르기"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDoubleClick={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); openPicker(e.currentTarget.nextSibling); }}
              >
                ▦
              </button>
              <input
                type="date"
                className="prg-cal-input"
                tabIndex={-1}
                value={row.values[col.key] || ''}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(e) => { setEditing(null); onCommit([{ row_id: row.id, key: col.key, value: e.target.value || null }]); }}
              />
            </>
          )}
        </>
      );
    }
    return (
      <td
        key={col.key}
        className={cls}
        style={tint ? { ...stickyStyle(c), ...tint } : stickyStyle(c)}
        onMouseDown={(e) => onCellMouseDown(r, c, e)}
        onDoubleClick={() => startEdit(r, c)}
      >
        {body}
      </td>
    );
  };

  return (
    <div className="prg">
      <style ref={hoverStyle} />
      <div className="prg-bar">
        <div className="prg-bar-row">
          <span className="prg-bar-title">컬럼 표시</span>
          <button
            type="button"
            className="prg-chip all"
            disabled={hiddenGroups.length === 0}
            title="숨긴 컬럼을 모두 다시 보입니다"
            onClick={() => { setHiddenGroups([]); save(STORE_HIDDEN, []); }}
          >
            모두 보기
          </button>
          {categories.map((cat) => (
            <span key={cat.name} className="prg-cat">
              {(cat.groups.length > 1 || cat.groups[0].label !== cat.name) && (
                <button
                  type="button"
                  className={`prg-cat-name ${cat.groups.every((g) => hiddenGroups.includes(g.name)) ? 'off' : ''}`}
                  title={cat.groups.every((g) => !hiddenGroups.includes(g.name)) ? `${cat.name} 컬럼 모두 숨기기` : `${cat.name} 컬럼 모두 보이기`}
                  onClick={() => toggleCategory(cat)}
                >
                  {cat.name}
                </button>
              )}
              {cat.groups.map((g) => {
                const on = !hiddenGroups.includes(g.name);
                return (
                  <button
                    type="button"
                    key={g.name}
                    role="switch"
                    aria-checked={on}
                    className={`prg-chip tone-${toneOf(g.name)} ${on ? '' : 'off'}`}
                    title={on ? `${g.name} 숨기기` : `${g.name} 보이기`}
                    onClick={() => toggleGroup(g.name)}
                  >
                    <i className="prg-check">{on ? '✓' : ''}</i>
                    {g.label}
                  </button>
                );
              })}
            </span>
          ))}
        </div>
        <div className="prg-bar-row">
          <span className="prg-bar-title">바로가기</span>
          {jumpTargets.map((g) => (
            <button
              type="button"
              key={g.name}
              className={`prg-jump tone-${toneOf(g.name)}`}
              title={`${g.name} 컬럼으로 이동`}
              onClick={() => scrollToGroup(g)}
            >
              {g.name.replace(' · ', ' ')}
            </button>
          ))}
          {/* 범례는 같은 줄의 오른쪽 끝에 붙인다 */}
          <span className="prg-legend">
            <span className="prg-bar-title">범례</span>
            {/* 선택한 칸에 적용된 항목만 또렷하게 두고 나머지는 흐리게 한다 */}
            {LEGEND.map((item) => (
              <LegendChip
                key={item.type}
                type={item.type}
                withNote
                className={activeTypes.size ? (activeTypes.has(item.type) ? 'lit' : 'dim') : ''}
              />
            ))}
          </span>
        </div>
        {/* 표에 영향을 주는 기능과 서류 단추(대장 화면이 넘겨 준다) */}
        {toolbar && <div className="prg-bar-row prg-bar-actions">{toolbar}</div>}
      </div>
      <div
        className="prg-scroll"
        ref={scrollRef}
        tabIndex={0}
        style={{ '--prg-pinned-w': `${pinnedW}px` }}
        onScroll={(e) => { hideTip(); setViewport({ top: e.currentTarget.scrollTop, height: e.currentTarget.clientHeight }); }}
        onKeyDown={(e) => { if (e.target === scrollRef.current && !editing) handleNavKey(e); }}
        onFocus={() => { gridFocused.current = true; }}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) gridFocused.current = false; }}
        onCopy={handleCopy}
        onPaste={handlePaste}
      >
        <table className="prg-table" style={{ width: tableW }}>
          <colgroup>
            <col style={{ width: ROWNUM_W }} />
            {cols.map((col, i) => <col key={col.key} style={{ width: widths[i] }} />)}
          </colgroup>
          <thead>
            <tr className="prg-groups">
              <th className="prg-corner" rowSpan={2}>순번</th>
              {groups.map((g) => (
                <th
                  key={`${g.name}-${g.start}`}
                  colSpan={g.span}
                  className={`group-start tone-${toneOf(g.name)} ${pinClass(g.start)}`}
                  style={g.pinned ? { left: lefts[g.start] } : undefined}
                  title={g.name}
                >
                  <span className="prg-group-label">
                    {g.pinned ? '📌' : g.name}
                    {!g.pinned && onAddColumn && addButtonOf(g.name) && (
                      <button
                        type="button"
                        className="prg-add-col"
                        title={addButtonOf(g.name).title}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => onAddColumn(addButtonOf(g.name).kind)}
                      >
                        {addButtonOf(g.name).label}
                      </button>
                    )}
                  </span>
                </th>
              ))}
            </tr>
            <tr className="prg-labels">
              {cols.map((col, c) => (
                <th
                  key={col.key}
                  title={[
                    `${col.group} › ${col.label}`,
                    col.hint,
                    col.key === ALWAYS_PINNED ? '누르면 열 전체 선택' : `누르면 열 전체 선택 · 오른쪽 단추를 누르면 ${c < pinCount ? '고정 해제' : '왼쪽에 고정'}`,
                  ].filter(Boolean).join('\n')}
                  className={[
                    `tone-${toneOf(col.group)}`,
                    col.editable && !col.auto ? '' : 'calc',
                    TOTAL_KEYS.has(col.key) ? 'total' : '',
                    c >= range.c0 && c <= range.c1 ? 'col-sel' : '',
                    c === active.c ? 'col-active' : '',
                    'selectable',
                    pinClass(c),
                    groupStarts.has(c) ? 'group-start' : '',
                  ].filter(Boolean).join(' ')}
                  style={stickyStyle(c)}
                  onClick={(e) => selectCol(c, e)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (col.key !== ALWAYS_PINNED) togglePin(col.key);
                  }}
                >
                  <div className="prg-label">{col.head || col.label}</div>
                  <div className="prg-sub">
                    {subTags(col).map((tag) => <span key={tag} className={`prg-tag ${TAG_TONE[tag] ? `tag-${TAG_TONE[tag]}` : ''}`}>{tag}</span>)}
                  </div>
                  <div className="prg-head-tools">
                    {/* 고정한 열에는 압정을, 아직 고정하지 않은 열에는 마우스를 올렸을 때 압정 그림자를 보인다.
                        고정·해제는 열 머리를 누르면 된다 */}
                    {col.key !== ALWAYS_PINNED && <span className={`prg-pin ${c < pinCount ? '' : 'ghost'}`}>📌</span>}
                    {onRemoveColumn && isRemovable(col) && (
                      <button
                        type="button"
                        className="prg-remove"
                        title={`'${col.label}' 열을 이 달 대장에서 빼기`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onRemoveColumn(col); }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div
                    className="prg-resize"
                    title="끌어서 너비 조절 · 두 번 누르면 내용에 맞춤"
                    onMouseDown={(e) => startResize(c, e)}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => autoFit(c, e)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody onMouseOver={onBodyHover} onMouseLeave={() => { showHoverCol(-1); hideTip(); }}>
            {first > 0 && <tr style={{ height: first * ROW_H }}><td colSpan={cols.length + 1} /></tr>}
            {rows.slice(first, last).map((row, i) => {
              const r = first + i;
              return (
                <tr key={row.id} data-r={r} style={{ height: ROW_H }} className={`${row.error ? 'has-error' : ''} ${r % 2 ? 'odd' : ''}`}>
                  <th
                    className={`prg-rownum ${r >= range.r0 && r <= range.r1 ? 'row-sel' : ''} ${r === active.r ? 'row-active' : ''}`}
                    title={row.error || ''}
                    onMouseDown={(e) => selectRow(r, e)}
                  >
                    {row.error ? '⚠' : row.order}
                  </th>
                  {cols.map((col, c) => renderCell(row, r, col, c))}
                </tr>
              );
            })}
            {last < rows.length && <tr style={{ height: (rows.length - last) * ROW_H }}><td colSpan={cols.length + 1} /></tr>}
          </tbody>
          <tfoot>
            <tr className="prg-total-row">
              <th className="prg-rownum">Σ</th>
              {cols.map((col, c) => (
                <td
                  key={col.key}
                  className={`${isNumeric(col.type) ? 'num' : ''} ${pinClass(c)} ${groupStarts.has(c) ? 'group-start' : ''}`}
                  style={stickyStyle(c)}
                >
                  {col.key === ALWAYS_PINNED ? `합계 ${rows.length}명` : totals[col.key] != null ? totals[col.key].toLocaleString('ko-KR') : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {/* 줄이 하나도 없을 때: 표의 틀(열 머리)은 그대로 두고 그 위에 안내를 띄운다 */}
      {rows.length === 0 && empty && <div className="prg-empty">{empty}</div>}
      <div className="prg-status">
        <span className="prg-status-cell">
          {activeCol ? `${activeCol.group} › ${activeCol.label}${activeCol.sub ? ` (${activeCol.sub})` : ''}` : ''}
        </span>
        <span className="prg-status-hint" title={activeTags.map((t) => `[${(LEGEND_BY_TYPE[t.type] || {}).label || t.type}] ${t.text}`).join('\n')}>
          {activeRow && activeRow.error
            ? `⚠ ${activeRow.error}`
            : activeTags.map((t) => (
              <span key={t.type} className="prg-status-tag">
                <LegendChip type={t.type} />
                {t.text}
              </span>
            ))}
        </span>
        <span className="prg-status-sum">
          {summary.numbers > 1 && `합계 ${Math.round(summary.sum).toLocaleString('ko-KR')} · `}
          {summary.count > 1 && `개수 ${summary.count}`}
        </span>
      </div>
      {memoTip && (
        <div className="prg-tip memo" style={{ top: memoTip.y, left: memoTip.x, width: TIP_W }}>
          <p className="prg-tip-memo">{memoTip.memo.text}</p>
          {memoTip.memo.updated && (
            <div className="prg-tip-foot">
              마지막 수정 {new Date(memoTip.memo.updated.at).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' })}
              {' · '}{memoTip.memo.updated.by_name || memoTip.memo.updated.by}
            </div>
          )}
        </div>
      )}
      {tip && tipRow && tipCol && (
        <div
          className="prg-tip"
          style={{ top: tip.y, width: TIP_W, ...(tip.flip ? { right: window.innerWidth - tip.x } : { left: tip.x }) }}
        >
          <div className="prg-tip-head">
            <b>{tipCol.label}</b>
            <span>{tipCol.group}{tipCol.sub ? ` · ${tipCol.sub}` : ''}</span>
          </div>
          {tipRow.error && <div className="prg-tip-error">⚠ {tipRow.error}</div>}
          {tipOption && tipOption.rows && (
            <div className="prg-tip-detail">
              <span className="prg-tip-chip" style={{ background: tipOption.color }}>{tipOption.label}</span>
              {tipOption.rows.length === 0 ? <p>근무시간표가 비어 있습니다</p> : (
                <table>
                  <thead><tr><th>구분</th><th>근무시간</th><th>휴게시간</th><th>비고</th></tr></thead>
                  <tbody>
                    {tipOption.rows.map((x, i) => <tr key={i}><td>{x.days}</td><td>{x.work}</td><td>{x.rest}</td><td>{x.note}</td></tr>)}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {tipTags.map((t) => (
            <div key={t.type} className="prg-tip-line">
              <LegendChip type={t.type} />
              {t.text && <p>{t.text}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PayrollGrid;
